import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useToast } from "@/components/ui/use-toast";
import { Loader2, MapPin, Calendar, Landmark } from "lucide-react";
import invitationImg from "@/assets/invitation-diner-senat.png";

const AMOUNT = 70;

const schema = z.object({
  lastName: z.string().trim().min(2, "Le nom doit contenir au moins 2 caractères").max(100),
  firstName: z.string().trim().min(2, "Le prénom doit contenir au moins 2 caractères").max(100),
  birthDate: z.string().min(1, "Date de naissance requise").refine((v) => !isNaN(Date.parse(v)), "Date invalide"),
  birthPlace: z.string().trim().min(2, "Lieu de naissance requis").max(150),
  email: z.string().trim().email("Email invalide").max(255),
  phone: z.string().trim().max(30).optional().or(z.literal("")),
});

type FormValues = z.infer<typeof schema>;

export default function SenatDinnerInscription() {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { lastName: "", firstName: "", birthDate: "", birthPlace: "", email: "", phone: "" },
  });

  const onSubmit = async (data: FormValues) => {
    setIsSubmitting(true);
    try {
      const { data: inserted, error } = await supabase.rpc("insert_senat_dinner_registration", {
        p_first_name: data.firstName,
        p_last_name: data.lastName,
        p_birth_date: data.birthDate,
        p_birth_place: data.birthPlace,
        p_amount: AMOUNT,
        p_email: data.email,
        p_phone: data.phone || null,
      });

      if (error) throw error;
      const reg = (inserted as any)?.[0] || inserted;

      const result = await supabase.functions.invoke("create-senat-dinner-payment", {
        body: {
          registrationId: reg.id,
          amount: AMOUNT,
          firstName: data.firstName,
          lastName: data.lastName,
        },
      });

      if (result.error) throw result.error;
      const payload = result.data as any;
      if (payload?.sessionUrl) {
        window.location.href = payload.sessionUrl;
      } else {
        throw new Error("Impossible de créer la session de paiement");
      }
    } catch (err: any) {
      console.error("Registration error:", err);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: err.message || "Une erreur est survenue lors de l'inscription.",
      });
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#E8F0F8] via-[#F5F8FC] to-[#E8F0F8]">
      <div className="bg-[#001233] py-4 px-6">
        <div className="container mx-auto flex items-center gap-3">
          <Landmark className="h-8 w-8 text-[#FFFD4C]" />
          <h1 className="text-xl md:text-2xl font-bold text-white font-acumin">
            Enfants de la République
          </h1>
        </div>
      </div>

      <main className="container mx-auto px-4 py-8 md:py-12">
        <div className="text-center mb-8">
          <h2 className="text-3xl md:text-5xl font-bold text-[#001233] mb-4">
            Dîner au Sénat
          </h2>
          <p className="text-lg md:text-xl text-[#001233]/80 mb-2">
            Présentation de l'Indice de la Citoyenneté Française (ICF 2025)
          </p>
          <p className="text-sm md:text-base text-[#001233]/70 italic mb-6">
            Sous le parrainage de Monsieur Bernard Fialaire, Sénateur du Rhône
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 md:gap-8 text-[#001233]/90">
            <div className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-[#00509E]" />
              <span className="text-lg font-medium">Mardi 16 juin 2026 — 19h15</span>
            </div>
            <div className="flex items-center gap-2">
              <MapPin className="h-5 w-5 text-[#00509E]" />
              <span className="text-lg font-medium">Palais du Luxembourg, Paris</span>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto items-start">
          <div className="order-2 md:order-1">
            <img
              src={invitationImg}
              alt="Invitation au dîner au Sénat du 16 juin 2026"
              className="w-full rounded-lg shadow-xl border border-black/10"
            />
          </div>

          <div className="order-1 md:order-2">
            <Card className="border-0 shadow-2xl">
              <CardContent className="pt-6 pb-8">
                <h3 className="text-xl font-bold text-center text-foreground mb-2">
                  Confirmation de présence
                </h3>
                <p className="text-sm text-muted-foreground text-center mb-4">
                  Participation aux frais : <span className="font-bold text-primary">{AMOUNT} €</span> par personne (dîner inclus)
                </p>
                <p className="text-xs text-center text-muted-foreground mb-2">
                  Places limitées à 50 personnes — confirmation avant le 8 juin 2026
                </p>
                <p className="text-xs text-center text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mb-6">
                  ⚠️ Cette participation ne bénéficie pas de l'abattement fiscal d'un don. Pour des raisons de sécurité, une pièce d'identité sera demandée à l'entrée.
                </p>

                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                    <FormField
                      control={form.control}
                      name="lastName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Nom *</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Votre nom"
                              autoComplete="family-name"
                              autoCapitalize="words"
                              autoCorrect="off"
                              spellCheck={false}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="firstName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Prénom *</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Votre prénom"
                              autoComplete="given-name"
                              autoCapitalize="words"
                              autoCorrect="off"
                              spellCheck={false}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="birthDate"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Date de naissance *</FormLabel>
                          <FormControl>
                            <Input
                              type="date"
                              autoComplete="bday"
                              max={new Date().toISOString().split("T")[0]}
                              min="1900-01-01"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="birthPlace"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Lieu de naissance *</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Ville (et pays si hors France)"
                              autoComplete="address-level2"
                              autoCapitalize="words"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Email *</FormLabel>
                          <FormControl>
                            <Input
                              type="email"
                              inputMode="email"
                              placeholder="votre@email.fr"
                              autoComplete="email"
                              autoCapitalize="none"
                              autoCorrect="off"
                              spellCheck={false}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="phone"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Téléphone</FormLabel>
                          <FormControl>
                            <Input
                              type="tel"
                              inputMode="tel"
                              placeholder="06 12 34 56 78"
                              autoComplete="tel"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full py-6 bg-[#00509E] hover:bg-[#003d7a] text-white text-lg font-semibold mt-4"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                          Redirection vers le paiement...
                        </>
                      ) : (
                        `Confirmer et payer ${AMOUNT} €`
                      )}
                    </Button>
                  </form>
                </Form>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      <footer className="py-6 text-center text-[#001233]/50 text-sm">
        <p>© 2026 Enfants de la République — Dîner au Sénat</p>
        <p className="mt-1">Contact : contact@edlr.fr | www.edlr.org</p>
      </footer>
    </div>
  );
}
